import { useState, useEffect } from "react";
import { createArticles, deleteArticle, getArticles, change_article } from "../api"; 
import { useAuth } from "../context/AuthContext";
import { Navigate } from 'react-router-dom';
const API_URL = import.meta.env.VITE_API_URL;

function AdminPanel() {
  const [title, setTitle] = useState("");
  const [resume, setResume] = useState("");
  const [content, setContent] = useState("");
  const [doi, setDoi] = useState("");
  const [date, setDate] = useState("");
  const { isAuthenticated, logout } = useAuth();
  const [adminData, setAdminData] = useState(null);
  const [articles, setArticles] = useState([]);
  const [editedArticle, setEditedArticle] = useState(null);

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}` 
          }
        });
      
        if (!response.ok) {
          throw new Error('Erro ao carregar dados do admin');
        }
      
        const data = await response.json();
        setAdminData(data);
      } catch (error) {
        console.error('Erro:', error);
      }
    };

    const fetchArticles = async () => {
      try {
        const data = await getArticles();
        setArticles(data || []);
      } catch (error) {
        console.error('Erro ao carregar artigos:', error);
      }
    };

    if (isAuthenticated) {
      fetchAdminData();
      fetchArticles();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    alert("Você não está autorizado a acessar esta página.");
    return <Navigate to='/login' replace />;
  }
   
  const handleDelete = async (id) => {
    try {
      await deleteArticle(id);
      alert("Artigo deletado com sucesso!");
      setArticles(articles.filter(article => article.id !== id));
    } catch(error) {
      console.error("Erro ao deletar o artigo", error);
      alert("Erro ao deletar artigo. Tente novamente em alguns minutos");
    }
  }

  const changeArticle = async (editedArticle) => {
    try {
      const updatedArticle = await change_article(editedArticle);
      alert("Publicação alterada com sucesso!");
      setArticles(articles.map(article => article.id === updatedArticle.id ? updatedArticle : article));
      setEditedArticle(null);
    } catch(error) {
      console.error("Erro ao alterar a publicação", error);
      alert("Erro ao alterar a publicação. Tente novamente em alguns minutos");
    }
  }

  const startEdit = (article) => {
    setEditedArticle(article);
  };

  const cancelEdit = () => {
    setEditedArticle(null);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const newArticle = { title, resume, content, doi, date };
      await createArticles(newArticle); 
      alert("Artigo criado com sucesso!");
      
      setTitle(""); setResume(""); setContent(""); setDoi(""); setDate("");
      
      const data = await getArticles();
      setArticles(data || []);
    } catch (error) {
      console.error("Erro ao criar artigo:", error);
      alert("Erro ao criar artigo. Tente novamente em alguns minutos");
    }
  };

  return (
    /* Trocado bg-Periodic por bg-background-secondary para respeitar o tema */
    <section className="min-h-screen bg-background-secondary transition-colors duration-300 pt-24 pb-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Cabeçalho do Admin */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-text-primary tracking-wide">
            Painel Administrativo
          </h1>
          <button
            onClick={logout}
            className="bg-red-500/10 text-red-500 border border-red-500/20 font-semibold px-6 py-2.5 rounded-lg hover:bg-red-500 hover:text-white transition-all"
          >
            Sair do Sistema
          </button>
        </div>

        {adminData ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* COLUNA ESQUERDA: Formulários (Adicionar ou Editar) */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              
              {!editedArticle ? (
                /* FORMULÁRIO DE ADIÇÃO */
                <div className="bg-surface border border-border-light rounded-xl shadow-card p-6 md:p-8 transition-colors duration-300">
                  <h2 className="text-2xl font-bold mb-6 text-text-primary border-b border-border-light pb-2">Novo Artigo</h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <input type="text" placeholder="Título do Artigo" value={title} onChange={(e) => setTitle(e.target.value)} className="input-field" required />
                    </div>
                    <div>
                      <textarea placeholder="Resumo breve" value={resume} onChange={(e) => setResume(e.target.value)} className="input-field resize-none" rows="3" required />
                    </div>
                    <div>
                      <textarea placeholder="Conteúdo completo" value={content} onChange={(e) => setContent(e.target.value)} className="input-field resize-none" rows="6" required />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input type="text" placeholder="DOI (Opcional)" value={doi} onChange={(e) => setDoi(e.target.value)} className="input-field" />
                      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-field" required />
                    </div>
                    <button type="submit" className="btn-primary w-full mt-2">
                      Publicar Artigo
                    </button>
                  </form>
                </div>
              ) : (
                /* FORMULÁRIO DE EDIÇÃO */
                <div className="bg-surface border border-border-light rounded-xl shadow-card p-6 md:p-8 border-t-4 border-t-primary transition-colors duration-300">
                  <div className="flex justify-between items-center mb-6 border-b border-border-light pb-2">
                    <h2 className="text-2xl font-bold text-text-primary">Editar Artigo</h2>
                    <button onClick={cancelEdit} className="text-text-muted hover:text-red-500 transition-colors">
                      Cancelar
                    </button>
                  </div>
                  
                  <form onSubmit={(e) => { e.preventDefault(); changeArticle(editedArticle); }} className="space-y-5">
                    <div>
                      <label className="block text-text-secondary text-sm font-bold mb-1">Título</label>
                      <input type="text" value={editedArticle.title} onChange={(e) => setEditedArticle({ ...editedArticle, title: e.target.value })} className="input-field" required />
                    </div>
                    <div>
                      <label className="block text-text-secondary text-sm font-bold mb-1">Resumo</label>
                      <textarea value={editedArticle.resume} onChange={(e) => setEditedArticle({ ...editedArticle, resume: e.target.value })} className="input-field resize-none" rows="3" required />
                    </div>
                    <div>
                      <label className="block text-text-secondary text-sm font-bold mb-1">Conteúdo</label>
                      <textarea value={editedArticle.content} onChange={(e) => setEditedArticle({ ...editedArticle, content: e.target.value })} className="input-field resize-none" rows="6" required />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-text-secondary text-sm font-bold mb-1">DOI</label>
                        <input type="text" value={editedArticle.doi} onChange={(e) => setEditedArticle({ ...editedArticle, doi: e.target.value })} className="input-field" />
                      </div>
                      <div>
                        <label className="block text-text-secondary text-sm font-bold mb-1">Data</label>
                        <input type="date" value={editedArticle.date} onChange={(e) => setEditedArticle({ ...editedArticle, date: e.target.value })} className="input-field" required />
                      </div>
                    </div>
                    <button type="submit" className="btn-primary w-full mt-2">
                      Salvar Alterações
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* COLUNA DIREITA: Lista de Artigos */}
            <div className="lg:col-span-7 bg-surface border border-border-light rounded-xl shadow-card p-6 md:p-8 flex flex-col max-h-[85vh] transition-colors duration-300">
              <h2 className="text-2xl font-bold mb-6 text-text-primary border-b border-border-light pb-2">Artigos Publicados ({articles.length})</h2>
              
              <div className="overflow-y-auto pr-2 space-y-4 custom-scrollbar">
                {articles.length > 0 ? (
                  articles.map((article) => (
                    <div key={article.id} className="bg-background p-5 rounded-lg border border-border-light shadow-sm hover:shadow-md transition-all duration-300">
                      <h3 className="text-lg font-bold text-text-primary mb-2">{article.title}</h3>
                      <p className="text-text-secondary text-sm mb-4 line-clamp-2">{article.resume}</p>
                      
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => startEdit(article)}
                          className="flex-1 sm:flex-none bg-primary/10 text-primary font-semibold px-4 py-2 rounded border border-primary/20 hover:bg-primary/20 transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="flex-1 sm:flex-none bg-red-500/10 text-red-500 font-semibold px-4 py-2 rounded border border-red-500/20 hover:bg-red-500/20 transition-colors"
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10 text-text-muted">
                    <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5L18.5 7H20a2 2 0 012 2v10a2 2 0 01-2 2h-2z" /></svg>
                    <p>Nenhum artigo publicado ainda.</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        ) : (
          <div className="flex justify-center items-center h-64 text-primary text-xl font-medium">
            <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            Carregando painel...
          </div>
        )}
      </div>
    </section>
  );
}

export default AdminPanel;